import { queryOptions, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useListaFiltrada } from "@/lib/filtros";
import { type FiltrosFuentes, filtrarFuentes } from "@/lib/filtros/fuentes";

/** Tipo de energía de una fuente (espejo del CHECK de la migración 000008). */
export type TipoFuente =
  | "ELECTRICA"
  | "NEUMATICA"
  | "HIDRAULICA"
  | "MECANICA"
  | "TERMICA"
  | "QUIMICA"
  | "OTRA";

/**
 * Fuente es una fuente de energía de un equipo con el contexto de su equipo/área
 * (GET /api/v1/fuentes). `codigo_fuente` (FE-XXXX) lo asigna el servidor; el cliente
 * nunca lo fija.
 */
export type Fuente = {
  id: number;
  id_equipo: number;
  codigo_equipo: string;
  nombre_equipo: string;
  nombre_area: string;
  codigo_fuente: string;
  nombre_fuente: string;
  tipo: TipoFuente;
  descripcion: string | null;
  activo: boolean;
  version: number;
  updated_at: string;
};

/** FuenteInput es el payload de alta/edición (POST/PUT /api/v1/fuentes). */
export type FuenteInput = {
  id_equipo: number;
  nombre_fuente: string;
  tipo: TipoFuente;
  descripcion: string | null;
  activo: boolean;
};

/**
 * fuentesQueryKey namespacea la caché de fuentes POR obra: `["obra", obraId, "fuentes"]`
 * (F7 #158). Aísla en la caché de Query las fuentes de una obra de las de otra.
 */
export function fuentesQueryKey(obraId: number) {
  return ["obra", obraId, "fuentes"] as const;
}

/**
 * fuentesQueryOptions lista las fuentes vivas con el contexto de su equipo de la obra
 * `obraId`. Se refresca invalidando la clave tras cada mutación, igual que el resto de
 * maestros de F2, ahora con la obra en la ruta y en la clave.
 */
export function fuentesQueryOptions(obraId: number) {
  return queryOptions({
    queryKey: fuentesQueryKey(obraId),
    queryFn: () => api.get<Fuente[]>(`/obras/${obraId}/fuentes`),
  });
}

/**
 * useFuentes lista las fuentes de la obra, opcionalmente FILTRADAS (#247): con red filtra
 * el servidor; sin red, la función pura sobre la lista base cacheada.
 */
export function useFuentes(obraId: number, filtros: FiltrosFuentes = {}) {
  return useListaFiltrada<Fuente>({
    claveBase: fuentesQueryKey(obraId),
    url: `/obras/${obraId}/fuentes`,
    filtros,
    filtrar: filtrarFuentes,
  });
}

export function useCreateFuente(obraId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: FuenteInput) => api.post<Fuente>(`/obras/${obraId}/fuentes`, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: fuentesQueryKey(obraId) }),
  });
}

export function useUpdateFuente(obraId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: FuenteInput }) =>
      api.put<Fuente>(`/obras/${obraId}/fuentes/${id}`, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: fuentesQueryKey(obraId) }),
  });
}

export function useDeleteFuente(obraId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.del<void>(`/obras/${obraId}/fuentes/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: fuentesQueryKey(obraId) }),
  });
}

/** Etiqueta legible de cada tipo de energía, para pintar tablas y selects. */
export const TIPO_FUENTE_LABEL: Record<TipoFuente, string> = {
  ELECTRICA: "Eléctrica",
  NEUMATICA: "Neumática",
  HIDRAULICA: "Hidráulica",
  MECANICA: "Mecánica",
  TERMICA: "Térmica",
  QUIMICA: "Química",
  OTRA: "Otra",
};
